import React, { useEffect, useState } from "react";
import { Mail, Trash2, MessageCircle, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700",
  contacted: "bg-amber-50 text-amber-700",
  converted: "bg-emerald-50 text-emerald-700",
  archived: "bg-slate-100 text-slate-500",
};

const STATUSES = ["new", "contacted", "converted", "archived"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="eyebrow mb-2">Inbox</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="leads-title">Leads</h1>
          <p className="text-slate-500 mt-2 text-sm">
            Captured from your referral landing pages.
          </p>
        </div>
      </div>

      <div className="bento overflow-hidden" data-testid="leads-list">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Message</th>
              <th className="px-6 py-4">Ref</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Received</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="px-6 py-16 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-16 text-center text-slate-500">
                  No leads yet. Share a referral link to start capturing inbound interest.
                </td>
              </tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 align-top" data-testid={`lead-row-${l.id}`}>
                <td className="px-6 py-4 font-medium text-slate-900">{l.name}</td>
                <td className="px-6 py-4 text-slate-600">
                  <a href={`mailto:${l.email}`} className="hover:underline inline-flex items-center gap-1">
                    {l.email} <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                </td>
                <td className="px-6 py-4 text-slate-600 text-sm max-w-xs">
                  {l.message ? (
                    <span className="line-clamp-3">{l.message}</span>
                  ) : (
                    <span className="text-slate-400 italic">no message</span>
                  )}
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{l.referral_code || "—"}</td>
                <td className="px-6 py-4">
                  <select
                    value={l.status}
                    onChange={(e) => updateStatus(l.id, e.target.value)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border-0 outline-none cursor-pointer ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                    data-testid={`lead-status-${l.id}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono whitespace-nowrap">
                  {new Date(l.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <a
                    href={`mailto:${l.email}?subject=${encodeURIComponent("Re: your enquiry")}`}
                    className="inline-flex items-center gap-1 text-sm text-white bg-slate-900 hover:bg-slate-700 px-3 py-1.5 rounded-full"
                    data-testid={`lead-reply-${l.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> Reply
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Hi ${l.name}, thanks for reaching out! `)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded-lg ml-1"
                    data-testid={`lead-whatsapp-${l.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => remove(l.id)}
                    className="inline-flex items-center gap-1 text-sm text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg ml-1"
                    data-testid={`lead-delete-${l.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
